import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihProgramList } from "~/components/cih/program-rows";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  getAllCcoCtpsFn,
  type CcoWithRelations,
  type CihCcoRow,
} from "~/lib/functions/cih/ccos";
import { createBrandMeta } from "~/lib/shared/meta";

type LoaderData = {
  title: string;
  cco: CihCcoRow;
  ctps: CcoWithRelations["ctps"];
};

export const Route = createFileRoute(
  "/_public/organizations/$slug/training-programs",
)({
  staticData: { title: "Training programs" },
  loader: async ({ params }): Promise<LoaderData> => {
    const { cco, ctps } = await getAllCcoCtpsFn({
      data: { slug: params.slug },
    });
    if (!cco) throw notFound();
    return { title: `${cco.name} training programs`, cco, ctps };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const name = loaderData?.cco.name ?? params.slug;
    return createBrandMeta({
      title: `${name} training programs | Dream Coach Match`,
      description: `Coach training programs delivered or accredited by ${name}.`,
      ogUrl: `https://dreamcoachmatch.com/organizations/${params.slug}/training-programs`,
      canonical: `https://dreamcoachmatch.com/organizations/${params.slug}/training-programs`,
      // Empty listing pages ("0 programs") are thin/orphan content that hurts
      // SEO and misrepresents our coverage to crawlers.
      noindexFollow: (loaderData?.ctps.length ?? 0) === 0,
    });
  },
  component: CcoTrainingProgramsPage,
});

function CcoTrainingProgramsPage() {
  const { cco, ctps } = Route.useLoaderData();
  const { slug } = Route.useParams();

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 pb-12 md:pt-10 md:pb-16 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Organizations", href: "/organizations" },
              { label: cco.name, href: `/organizations/${slug}` },
              { label: "Training programs" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● {cco.name}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl lg:text-5xl xl:text-6xl">
            {ctps.length} {cco.name} training programs
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <Link
              to="/organizations/$slug/coaches"
              params={{ slug }}
              className="font-medium text-purple-700 hover:text-purple-800"
            >
              See coaches →
            </Link>
          </div>
        </div>
      </section>

      <CihProgramList programs={ctps} />

      <CihClosingCta
        headline={`Find a coach trained at a ${cco.name} program.`}
        cta={
          <Link to="/discovery" search={{ source: `cco:${slug}:programs` }}>
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
